import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, SegmentedControl, StatusPill, cn } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { fmtDateShort } from '../lib/fmt';
import { CampaignForm, CaptureFormForm, type CampaignRow, type CaptureFormRow } from '../components/MarketingForms';

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/capture-lead`;

type Submission = { id: string; name: string | null; email: string | null; status: string; utm_source: string | null; created_at: string };

function embedSnippet(form: CaptureFormRow): string {
  const consent = form.consent_text || 'I agree to be contacted.';
  const redirect = form.redirect_url ? `location.href=${JSON.stringify(form.redirect_url)};` : '';
  return `<form onsubmit="return pvq(event)">
  <input name="name" placeholder="Name" required>
  <input name="email" type="email" placeholder="Email" required>
  <input name="phone" placeholder="Phone">
  <textarea name="message" placeholder="Message"></textarea>
  <input type="text" name="hp" style="position:absolute;left:-9999px" tabindex="-1" autocomplete="off" aria-hidden="true">
  ${form.require_consent ? `<label><input type="checkbox" name="consent" required> ${consent}</label>` : ''}
  <button type="submit">Send</button>
</form>
<script>
function pvq(e){e.preventDefault();var f=e.target,p=new URLSearchParams(location.search);
var u={utm_source:p.get('utm_source'),utm_medium:p.get('utm_medium'),utm_campaign:p.get('utm_campaign'),utm_term:p.get('utm_term'),utm_content:p.get('utm_content')};
fetch(${JSON.stringify(ENDPOINT)},{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({
  slug:${JSON.stringify(form.slug)},name:f.name.value,email:f.email.value,phone:f.phone.value,message:f.message.value,
  consent:${form.require_consent ? 'f.consent.checked' : 'true'},hp:f.hp.value,utm:u,page_url:location.href,referrer_url:document.referrer})})
.then(function(r){return r.json()}).then(function(j){if(j.ok){f.reset();${redirect}}else{alert(j.error||'Error')}});return false}
</script>`;
}

export function Marketing() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(0);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [forms, setForms] = useState<(CaptureFormRow & { submit_count: number })[]>([]);
  const [subs, setSubs] = useState<Submission[]>([]);
  const [selForm, setSelForm] = useState<string | null>(null);
  const [cForm, setCForm] = useState(false);
  const [editC, setEditC] = useState<CampaignRow | null>(null);
  const [fForm, setFForm] = useState(false);
  const [editF, setEditF] = useState<CaptureFormRow | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const [c, f] = await Promise.all([
      supabase.from('campaigns').select('id,name,channel,utm_source,utm_medium,utm_campaign,starts_on,ends_on,budget,active,notes').is('deleted_at', null).order('name'),
      supabase.from('capture_forms').select('id,name,slug,target_stage_id,source_id,campaign_id,require_consent,consent_text,redirect_url,active,submit_count').is('deleted_at', null).order('name'),
    ]);
    setCampaigns((c.data as CampaignRow[]) ?? []);
    const fl = (f.data as any[]) ?? [];
    setForms(fl);
    setSelForm((cur) => cur ?? fl[0]?.id ?? null);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selForm) { setSubs([]); return; }
    supabase.from('form_submissions').select('id,name,email,status,utm_source,created_at').eq('form_id', selForm).is('deleted_at', null).order('created_at', { ascending: false }).limit(20)
      .then((r) => setSubs((r.data as Submission[]) ?? []));
  }, [selForm]);

  const current = useMemo(() => forms.find((f) => f.id === selForm) ?? null, [forms, selForm]);

  function copySnippet() {
    if (!current) return;
    navigator.clipboard?.writeText(embedSnippet(current));
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Layout header={<Topbar actions={tab === 0 ? <Button onClick={() => { setEditC(null); setCForm(true); }}>+ {t('mkt.newCampaign')}</Button> : <Button onClick={() => { setEditF(null); setFForm(true); }}>+ {t('mkt.newForm')}</Button>} />}>
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.marketing')}</h1>
          <SegmentedControl items={[{ label: t('mkt.campaigns') }, { label: t('mkt.forms') }]} active={tab} onChange={setTab} />
        </div>
      </div>

      {tab === 0 ? (
        <div className="px-7 pb-7 pt-[18px]">
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-medium">{t('form.name')}</th>
                  <th className="px-2 py-3 font-medium">{t('mkt.channel')}</th>
                  <th className="px-2 py-3 font-medium">utm_campaign</th>
                  <th className="px-2 py-3 font-medium">{t('crm.active')}</th>
                  <th className="px-5 py-3" />
                </tr></thead>
                <tbody>
                  {campaigns.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
                  {campaigns.map((c) => (
                    <tr key={c.id} className="border-t border-divider hover:bg-bg">
                      <td className="px-5 py-3.5 font-medium">{c.name}</td>
                      <td className="px-2 py-3.5 text-muted">{c.channel ?? '—'}</td>
                      <td className="px-2 py-3.5 font-mono text-xs text-muted">{c.utm_campaign ?? '—'}</td>
                      <td className="px-2 py-3.5">{c.active ? <StatusPill tone="done">{t('common.yes')}</StatusPill> : <StatusPill tone="open">{t('common.no')}</StatusPill>}</td>
                      <td className="px-5 py-3.5 text-right"><Button variant="ghost" size="sm" onClick={() => { setEditC(c); setCForm(true); }}>{t('form.edit')}</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-[18px] px-7 pb-7 pt-[18px]">
          <Card className="min-w-0 max-w-[340px] flex-1 overflow-hidden p-0" style={{ flexBasis: 300 }}>
            {forms.length === 0 && <div className="p-6 text-sm text-muted">{t('common.noData')}</div>}
            {forms.map((f) => (
              <button key={f.id} onClick={() => setSelForm(f.id)} className={cn('flex w-full items-center justify-between gap-2 border-b border-divider p-4 text-left last:border-0', f.id === selForm ? 'bg-[#F5F7FF] shadow-[inset_3px_0_0_var(--pv-accent)]' : 'hover:bg-bg')}>
                <span className="flex flex-col"><span className="text-sm font-medium">{f.name}</span><span className="font-mono text-2xs text-muted">/{f.slug}</span></span>
                <span className="flex items-center gap-2">{!f.active && <span className="rounded-pill bg-bg px-1.5 py-0.5 text-2xs text-muted">{t('crm.active')}: {t('common.no')}</span>}<span className="font-mono text-xs text-muted">{f.submit_count}</span></span>
              </button>
            ))}
          </Card>

          {current && (
            <Card className="min-w-0 flex-[2] p-5" style={{ flexBasis: 480 }}>
              <div className="flex items-start justify-between gap-3">
                <div><h2 className="text-[18px] font-semibold">{current.name}</h2><span className="font-mono text-xs text-muted">/{current.slug}</span></div>
                <Button variant="secondary" size="sm" onClick={() => { setEditF(current); setFForm(true); }}>{t('form.edit')}</Button>
              </div>

              <div className="mt-4 flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted">{t('mkt.endpoint')}</span>
                <code className="block overflow-x-auto rounded-md bg-[#F7F8FA] px-3 py-2 font-mono text-xs">{ENDPOINT}</code>
              </div>

              <div className="mt-4 flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted">{t('mkt.embedSnippet')}</span>
                  <Button variant="ghost" size="sm" onClick={copySnippet}>{copied ? t('mkt.copied') : t('mkt.copy')}</Button>
                </div>
                <textarea readOnly value={embedSnippet(current)} className="h-40 w-full rounded-md border border-border bg-[#F7F8FA] p-3 font-mono text-2xs" />
                <span className="text-2xs text-muted">{t('mkt.embedHint')}</span>
              </div>

              <h3 className="mt-5 text-[15px] font-semibold">{t('mkt.submissions')}</h3>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <tbody>
                    {subs.length === 0 && <tr><td className="py-4 text-muted">{t('common.noData')}</td></tr>}
                    {subs.map((s) => (
                      <tr key={s.id} className="border-t border-divider first:border-0">
                        <td className="py-2.5 pr-2 font-medium">{s.name ?? '—'}</td>
                        <td className="py-2.5 pr-2 font-mono text-xs text-muted">{s.email ?? '—'}</td>
                        <td className="py-2.5 pr-2 text-xs text-muted">{s.utm_source ?? '—'}</td>
                        <td className="py-2.5 pr-2">{s.status === 'spam' ? <StatusPill tone="overdue">{t('mkt.spam')}</StatusPill> : <StatusPill tone="done">{t('mkt.' + (s.status === 'converted' ? 'converted' : 'new'))}</StatusPill>}</td>
                        <td className="py-2.5 text-right font-mono text-2xs text-muted">{fmtDateShort(s.created_at, i18n.language)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      <CampaignForm open={cForm} onClose={() => setCForm(false)} onSaved={load} initial={editC} />
      <CaptureFormForm open={fForm} onClose={() => setFForm(false)} onSaved={load} initial={editF} />
    </Layout>
  );
}
